import React, { useState, useEffect } from 'react';
import {
    Box,
    Alert,
    Card,
    Skeleton,
    useTheme
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../utils/constants';




// --- Custom Tooltip for Recharts ---
// This creates a tooltip with a style similar to your original Chart.js version.
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div style={{
                backgroundColor: 'rgba(30, 36, 44, 0.9)',
                color: '#CBD5E0',
                border: '1px solid #4A5568',
                borderRadius: '4px',
                padding: '10px'
            }}>
                <p style={{ color: '#E2E8F0', marginBottom: '5px' }}>{label}</p>
                <p>{`${ payload[0].name } : ${ payload[0].value }`}</p>
            </div>
        );
    }
    return null;
};


const BtcTransfersChart = () => {
    const { t } = useTranslation();
    // Recharts expects an array of objects, so we'll initialize with an empty array.
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [interval] = useState('day');
    const theme = useTheme();

    // This state will hold the computed maximum for the Y-axis domain.
    const [yAxisMax, setYAxisMax] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // This is your original fetch call.
                // To make this example runnable, it's commented out and mock data is used below.
                const resp = await fetch(
                    `${ API_BASE_URL }/transfers/btc-analytics?interval=${ interval }`
                );
                if (!resp.ok) {
                    const err = await resp.json();
                    throw new Error(err.error || `HTTP ${ resp.status }`);
                }
                const result = await resp.json();


                if (!result || result.length === 0) {
                    setChartData([]);
                    setYAxisMax(0);
                    return;
                }

                // Format data for Recharts: an array of objects.
                const formattedData = result.map(item => {
                    const date = new Date(item.time);
                    let label = '';
                    switch (interval) {
                        case 'hour':
                            // e.g., "5 PM"
                            label = date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
                            break;
                        case 'day':
                            // e.g., "Aug 24"
                            label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            break;
                        default:
                            label = date.toISOString();
                    }
                    return {
                        label: label,
                        activeUsers: item.activeUsers,
                    };
                });

                // Find the maximum value to give the Y-axis some padding, similar to your original logic.
                const maxUsers = Math.max(...result.map(i => i.activeUsers));
                setYAxisMax(Math.ceil((maxUsers * 1.2))); // Pad by 20%

                setChartData(formattedData);

            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [interval]);

    // Determine label-spacing for the X axis ticks
    const total = chartData.length;
    const quarter = total > 0 ? Math.ceil(total / 4) : 1;

    return (
        <Card sx={{ width: '100%', height: '100%', flex: 1 }}>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 2, // Add some padding
                }}
            >
                {loading && (
                    <Skeleton variant="rectangular" width={"100%"} height={"100%"} sx={{ bgcolor: 'grey.900' }} />
                )}

                {error && (
                    <Box sx={{ flex: 1, p: 2 }}>
                        <Alert severity="error">{t('analytics.errorFetchingData')}: {error}</Alert>
                    </Box>
                )}

                {!loading && !error && chartData.length === 0 && (
                    <Box sx={{ flex: 1, p: 2 }}>
                        <Alert severity="info">{t('analytics.noDataAvailableInterval')}</Alert>
                    </Box>
                )}

                {!loading && !error && chartData.length > 0 && (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={chartData}
                            margin={{
                                top: 5,
                                right: 20,
                                left: -10,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                            {/* <XAxis
                                dataKey="label"
                                stroke="#E2E8F0"
                                tickLine={false}
                                tickFormatter={(value, index) => index % quarter === 0 ? value : ''}
                                angle={-30}
                                textAnchor="end"
                                height={50}
                            />
                            <YAxis
                                stroke="#E2E8F0"
                                tickLine={false}
                                domain={[0, yAxisMax]}
                            /> */}
                            <XAxis dataKey="label" tickFormatter={(value, index) => index % quarter === 0 ? value : ''} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                            <YAxis  tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />

                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(113, 128, 150, 0.1)' }} />
                            <Legend verticalAlign="top" wrapperStyle={{ top: -5, fontSize: '12px' }} />
                            <Bar
                                dataKey="activeUsers"
                                name={t('analytics.activeUsers')}
                                fill="rgba(255,165,0,0.4)"
                                stroke="#FFA500"
                            />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Box>
        </Card>
    );
};

export default BtcTransfersChart;
